import { Injectable } from '@angular/core';

/**
 * A complete, shareable description of a flight search, parsed from the URL.
 * Everything here is enough to re-run the exact same search on a fresh device.
 */
export interface SearchDescriptor {
  fromIata: string;
  toIata: string;
  onward: string;            // yyyy-MM-dd
  return?: string;           // yyyy-MM-dd (round trips only)
  adults: number;
  children: number;
  infants: number;
  travelClass: string;
  tripType: 'oneway' | 'return';
}

/**
 * Builds and parses the booking-page URL so a search can be shared and
 * reproduced from the address bar alone.
 *
 * Path slug (e.g. /booking/nairobi-to-mombasa) is purely cosmetic/SEO-friendly;
 * the readable query params are the source of truth for the search.
 */
@Injectable({ providedIn: 'root' })
export class SearchUrlService {

  /** Turn a city/airport name into a clean URL slug, e.g. "New York" -> "new-york". */
  slugify(text: string): string {
    return (text || '')
      .toString()
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')   // non-alphanumerics -> single hyphen
      .replace(/^-+|-+$/g, '');      // trim leading/trailing hyphens
  }

  /** e.g. buildRouteSlug('Nairobi', 'Mombasa') -> "nairobi-to-mombasa". */
  buildRouteSlug(fromCity: string, toCity: string): string {
    const from = this.slugify(fromCity) || 'from';
    const to = this.slugify(toCity) || 'to';
    return `${from}-to-${to}`;
  }

  /**
   * Build the readable query params from a search payload (the `formdata`
   * shape used across home/booking).
   */
  buildQueryParams(search: any): { [key: string]: string } {
    const fromIata = search?.from_data?.iata || search?.depature_city || '';
    const toIata = search?.destination_data?.iata || search?.destination_city || '';
    const returning = !!search?.returning || search?.trip_type === 'return';

    const params: { [key: string]: string } = {
      from: fromIata,
      to: toIata,
      onward: search?.departure_on || '',
      adults: String(search?.adult_count ?? 1),
      children: String(search?.child_count ?? 0),
      infants: String(search?.infant_count ?? 0),
      class: search?.travel_class || 'economy',
      type: returning ? 'return' : 'oneway',
    };

    if (returning && search?.arrival_on) {
      params['return'] = search.arrival_on;
    }
    return params;
  }

  /**
   * Parse query params into a SearchDescriptor. Returns null when the params
   * don't describe a complete, runnable search (so callers can fall back).
   */
  parse(params: { [key: string]: any } | null | undefined): SearchDescriptor | null {
    if (!params) { return null; }

    const fromIata = (params['from'] || '').toString().trim().toUpperCase();
    const toIata = (params['to'] || '').toString().trim().toUpperCase();
    const onward = (params['onward'] || '').toString().trim();

    if (!fromIata || !toIata || !onward) { return null; }

    const tripType: 'oneway' | 'return' = params['type'] === 'return' ? 'return' : 'oneway';
    const ret = (params['return'] || '').toString().trim();

    return {
      fromIata,
      toIata,
      onward,
      return: tripType === 'return' ? (ret || onward) : undefined,
      adults: this.toInt(params['adults'], 1),
      children: this.toInt(params['children'], 0),
      infants: this.toInt(params['infants'], 0),
      travelClass: (params['class'] || 'economy').toString(),
      tripType,
    };
  }

  private toInt(value: any, fallback: number): number {
    const n = parseInt(value, 10);
    return Number.isFinite(n) && n >= 0 ? n : fallback;
  }
}
